import { Ref, forwardRef, useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { PersonFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { Button } from '@/components/Button'

import { ContentfulImage } from '../../../common/ContentfulImage'
import { ContentfulRichText } from '../../../common/ContentfulRichText'

type Props = {
	className?: string
	type?: 'Leader' | 'Advisor'
	size?: 'small' | 'large'
	priority?: (string | undefined)[]
	onlyShowPriority?: boolean
}

export const limit = 20
const companyLeaderLinkLocales = ['en-US', 'en-GB', 'en-AU']

export const PersonFeed = forwardRef(function PersonFeed(
	{ className, type = 'Leader', size = 'large', priority, onlyShowPriority = false }: Props,
	ref: Ref<HTMLUListElement>
) {
	const { locale = 'en-US', isPreview } = useRouter()
	const [skip, setSkip] = useState(0)

	const { data, isLoading } = useSWR(
		serializeCacheKey('personFeed', {
			type,
			limit,
			skip,
			locale
		}),
		() =>
			client({ preview: isPreview }).request(PersonFeedDocument, {
				where: { type, enabled: true },
				limit,
				skip,
				locale,
				preview: isPreview
			})
	)
	const [total, setTotal] = useState(data?.personCollection?.total ?? 0)
	const [items, setItems] = useState(data?.personCollection?.items ?? [])

	useEffect(() => {
		setItems((prev) => [
			...prev.slice(0, skip),
			...(data?.personCollection?.items ?? []),
			...prev.slice(skip + limit)
		])
	}, [data, skip])

	useEffect(() => {
		if (!data || !data.personCollection) return

		setTotal(data.personCollection.total)
	}, [data])

	return (
		<ul
			ref={ref}
			data-testid="person-feed"
			className={clsx('grid grid-cols-12 gap-x-8 gap-y-16', className)}
		>
			{items
				.slice()
				.filter((item) => !onlyShowPriority || priority?.includes(item?.sys.id))
				.sort((a, b) => {
					const ai = (priority ?? []).findIndex((id) => id && id === a?.sys.id)
					const bi = (priority ?? []).findIndex((id) => id && id === b?.sys.id)

					if (ai > -1 && bi === -1) return -1
					else if (ai === -1 && bi > -1) return 1
					else return ai - bi
				})
				.map(
					(item) =>
						item &&
						(item.additionalOptions?.avatarOverride ?? item.avatar) && (
							<li
								key={item.sys.id}
								data-testid="person-feed-item"
								className={clsx(
									'col-span-12 sm:col-span-6',
									{ small: 'md:col-span-3', large: 'md:col-span-4' }[size]
								)}
							>
								<ContentfulImage
									field={{ data: item.additionalOptions?.avatarOverride ?? item.avatar }}
								/>
								<div className="space-y-2.5 mt-14">
									<h3 className="text-h5 text-bc-black">{item.fullName}</h3>
									<h4 className="text-sm text-gray-600">
										{item.jobTitle || item.jobTitleInEnglish}
									</h4>
									{item.additionalOptions?.bioSummary && (
										<div className="text-gray-600 leading-7 text-[1.125rem]">
											<ContentfulRichText field={{ data: item.additionalOptions.bioSummary }} />
										</div>
									)}
									{companyLeaderLinkLocales.includes(locale) && type === 'Leader' && (
										<Button
											color="primary"
											size="large"
											link={{ href: `/company/leaders/${item.slug}`, target: '_self' }}
											showIcon
											variant="subtle"
											className="mt-14 uppercase text-bc-black"
										>
											View bio
										</Button>
									)}
								</div>
							</li>
						)
				)}
			{items.length < total && (
				<div className="flex justify-center col-span-12">
					<Button
						onClick={() => {
							if (items.length < total) {
								setSkip((prev) => prev + limit)
							}
						}}
						disabled={isLoading}
						size="large"
						color="secondary"
						variant="filled"
						isLoading={isLoading}
					>
						View more
					</Button>
				</div>
			)}
		</ul>
	)
})
